import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveLanguageDisplayName } from '@/shared/constants/languages'

/**
 * Resolves language codes to names in the dashboard's current UI locale.
 *
 * The locale must arrive through this hook rather than being read from the i18next
 * singleton inside the resolver: a hidden global read is invisible to React, so the
 * compiler memoizes label lists on the language array alone and they keep their old
 * wording after the user switches the dashboard language. Returning a callback that
 * closes over a reactive locale string makes the dependency explicit.
 *
 * @returns A stable-per-locale function mapping a language code to its display name.
 */
export function useLanguageLabel(): (code: string) => string {
  const { i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en'

  return useCallback((code: string) => resolveLanguageDisplayName(code, locale), [locale])
}
